import React from 'react';

/**
 * BulkActionBar — the app-wide standard bulk-action bar.
 *
 * Design (option 4): when rows are selected, this bar REPLACES the page's tab
 * strip in place — it does not push the list down. Tabs return on their own when
 * the selection is cleared. Dark green, fills the same h-10 tab-strip row.
 *
 * Usage: render conditionally as the AppShell `pageStrip`:
 *   const pageStrip = selected.size > 0
 *     ? <BulkActionBar count={selected.size} onClear={...} actions={[...]} />
 *     : <YourTabStrip/>;
 *
 * Props:
 *   count    — number selected (shown as "N selected")
 *   onClear  — called by the ✕ button (clears selection → tabs reappear)
 *   busy     — disables action buttons while an operation runs
 *   actions  — [{ label, onClick, danger?: bool }]
 */
export default function BulkActionBar({ count, onClear, busy = false, actions = [], children = null }) {
  return (
    <div
      className="h-10 flex items-center gap-1.5 px-4 animate-[bulkbar-in_150ms_ease-out]"
      style={{ backgroundColor: '#065F46', color: '#fff' }}
      role="toolbar"
      aria-label="Bulk actions"
    >
      <style>{`@keyframes bulkbar-in { from { opacity: 0; transform: translateY(-4px);} to { opacity: 1; transform: translateY(0);} }`}</style>
      <span className="text-xs font-semibold whitespace-nowrap">{count} selected</span>
      <div className="h-4 w-px bg-white/25 mx-1" />
      {children}
      {actions.map((a) => (
        <button
          key={a.label}
          onClick={a.onClick}
          disabled={busy}
          className="text-xs px-2.5 py-1 rounded transition-colors disabled:opacity-50 text-white/90 hover:bg-white/15"
        >
          {a.label}
        </button>
      ))}
      <button
        onClick={onClear}
        aria-label="Clear selection"
        title="Clear selection"
        className="ml-auto p-1 rounded text-white/70 hover:text-white hover:bg-white/15 transition-colors"
      >
        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
        </svg>
      </button>
    </div>
  );
}
